import { useCallback, useMemo, useState } from "react";
import { useIsHalloweenSeason } from "@/hooks/useIsHalloweenSeason";
import {
	type AssistantAvatarConfig,
	AssistantAvatarKind,
	AssistantAvatarMood,
	type AssistantCharacterAvatar,
	type AssistantHumanAvatar,
} from "@/ui/AssistantAvatar/assistantAvatarConfig";
import {
	ANNIE_AVATAR_PRESETS,
	type AnnieAvatarPreset,
	type AnnieAvatarPresetId,
	DEFAULT_CHARACTER_AVATAR,
	DEFAULT_HUMAN_AVATAR,
	defaultCharacterAvatar,
	HALLOWEEN_AVATAR_PRESETS,
} from "./annieAvatarPresets";
import { type CharacterSlot, changedSlots, type HumanSlot, isSameAvatar, randomAvatarLike } from "./annieStudioSlots";

/** What the user last did, so the stage can react to it. */
export enum AnnieStudioChange {
	Opened = "opened",
	Preset = "preset",
	Style = "style",
	Part = "part",
	Surprise = "surprise",
	Reset = "reset",
}

interface DraftState {
	draft: AssistantAvatarConfig;
	/** The last whole-design replacement; hand edits are measured from it. */
	baseline: AssistantAvatarConfig;
	presetId: AnnieAvatarPresetId | null;
	lastChange: AnnieStudioChange;
	/** 0 until the user changes the design, then bumps per change. */
	revision: number;
	/** Whether the draft was seeded from loaded settings (false = seeded before they arrived). */
	seededFromSaved: boolean;
	previewMood: AssistantAvatarMood;
	moodRevision: number;
}

/** `annie_avatar_saved` properties; analytics values are scalars, so the slots travel comma-joined. */
export interface AnnieAvatarSavedEvent {
	kind: AssistantAvatarKind;
	preset_used: AnnieAvatarPresetId | null;
	changed_slots: string;
	changed_slot_count: number;
}

/** `undefined` = settings not loaded yet; `null` = loaded, no saved design. */
type SavedDesign = AssistantAvatarConfig | null | undefined;

const ALL_PRESETS = [...ANNIE_AVATAR_PRESETS, ...HALLOWEEN_AVATAR_PRESETS];

const initialState = (saved: SavedDesign, startingLook: AssistantAvatarConfig): DraftState => {
	// DEFAULT: no saved design (or not loaded yet), so the draft starts from the look people see without one.
	const start = saved ?? startingLook;
	return {
		draft: start,
		baseline: start,
		presetId: null,
		lastChange: AnnieStudioChange.Opened,
		revision: 0,
		seededFromSaved: saved !== undefined,
		previewMood: AssistantAvatarMood.Idle,
		moodRevision: 0,
	};
};

/** Re-seeds on every open, and once more if the saved design arrives before the user has touched the draft. */
const useSeededState = (open: boolean, saved: SavedDesign, startingLook: AssistantAvatarConfig) => {
	const [state, setState] = useState(() => initialState(saved, startingLook));
	const [wasOpen, setWasOpen] = useState(open);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) setState(initialState(saved, startingLook));
	} else if (open && saved !== undefined && !state.seededFromSaved && state.revision === 0) {
		setState(initialState(saved, startingLook));
	}
	return [state, setState] as const;
};

/** The Studio's unsaved design plus the stage's preview mood. */
export const useAnnieStudioDraft = ({ open, saved }: { open: boolean; saved: SavedDesign }) => {
	const startingLook = defaultCharacterAvatar(useIsHalloweenSeason());
	const [state, setState] = useSeededState(open, saved, startingLook);

	const replace = useCallback(
		(draft: AssistantAvatarConfig, lastChange: AnnieStudioChange, presetId: AnnieAvatarPresetId | null) => {
			setState((prev) => ({ ...prev, draft, baseline: draft, presetId, lastChange, revision: prev.revision + 1 }));
		},
		[setState],
	);

	const applyPreset = useCallback((preset: AnnieAvatarPreset) => replace(preset.config, AnnieStudioChange.Preset, preset.id), [replace]);

	const setKind = useCallback(
		(kind: AssistantAvatarKind) => {
			if (kind === state.draft.kind) return;
			replace(kind === AssistantAvatarKind.Human ? DEFAULT_HUMAN_AVATAR : DEFAULT_CHARACTER_AVATAR, AnnieStudioChange.Style, null);
		},
		[replace, state.draft.kind],
	);

	const setCharacterPart = useCallback(
		<Slot extends CharacterSlot>(slot: Slot, value: AssistantCharacterAvatar[Slot]) => {
			setState((prev) =>
				prev.draft.kind === AssistantAvatarKind.Character
					? { ...prev, draft: { ...prev.draft, [slot]: value }, lastChange: AnnieStudioChange.Part, revision: prev.revision + 1 }
					: prev,
			);
		},
		[setState],
	);

	const setHumanPart = useCallback(
		<Slot extends HumanSlot>(slot: Slot, value: AssistantHumanAvatar[Slot]) => {
			setState((prev) =>
				prev.draft.kind === AssistantAvatarKind.Human
					? { ...prev, draft: { ...prev.draft, [slot]: value }, lastChange: AnnieStudioChange.Part, revision: prev.revision + 1 }
					: prev,
			);
		},
		[setState],
	);

	const randomize = useCallback(() => replace(randomAvatarLike(state.draft, Math.random), AnnieStudioChange.Surprise, null), [replace, state.draft]);
	const resetDraft = useCallback(() => replace(startingLook, AnnieStudioChange.Reset, null), [replace, startingLook]);
	const setPreviewMood = useCallback(
		(previewMood: AssistantAvatarMood) => setState((prev) => ({ ...prev, previewMood, moodRevision: prev.moodRevision + 1 })),
		[setState],
	);

	const { draft, baseline, presetId } = state;
	const savedEvent = useMemo<AnnieAvatarSavedEvent>(() => {
		const slots = changedSlots(baseline, draft);
		return { kind: draft.kind, preset_used: presetId, changed_slots: slots.join(","), changed_slot_count: slots.length };
	}, [draft, baseline, presetId]);

	return {
		...state,
		matchingPresetId: ALL_PRESETS.find((preset) => isSameAvatar(preset.config, draft))?.id ?? null,
		/** With no saved design, saving always changes something: it turns the avatar on. */
		isDirty: saved !== undefined && (saved === null || !isSameAvatar(draft, saved)),
		isDefaultDraft: isSameAvatar(draft, startingLook),
		savedEvent,
		applyPreset,
		setKind,
		setCharacterPart,
		setHumanPart,
		randomize,
		resetDraft,
		setPreviewMood,
	};
};

export type AnnieStudioDraft = ReturnType<typeof useAnnieStudioDraft>;
